
import { useState, useCallback, useEffect } from 'react';
import { Folder, Heart, Plus } from 'lucide-react';
import { useHybridVideos } from '@/hooks/useHybridVideos';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import FavoritesList from '@/components/playlists/FavoritesList';
import PlaylistSelector from '@/components/playlists/PlaylistSelector';
import PlaylistContent from '@/components/playlists/PlaylistContent';
import RecommendedVideos from '@/components/playlists/RecommendedVideos';
import PlaylistDialog from '@/components/playlists/PlaylistDialog';
import { PlaylistProvider, usePlaylistContext } from '@/contexts/PlaylistContext';

const PlaylistsPageContent = () => {
  const { videos, loading } = useHybridVideos('all');
  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Utiliser le contexte pour les données en temps réel
  const { 
    playlists, 
    playlistVideos, 
    favorites,
    isLoading: isLoadingPlaylists 
  } = usePlaylistContext();
  
  // Sélectionner la première playlist si aucune n'est sélectionnée
  useEffect(() => {
    if (playlists.length > 0 && !selectedPlaylist) {
      setSelectedPlaylist(playlists[0]?.id || null);
    }
  }, [playlists, selectedPlaylist]);
  
  const toggleFavorite = async (videoId: string) => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to add favorites',
        variant: 'destructive',
      });
      return;
    }
    
    const isFavorite = favorites.includes(videoId);
    
    try {
      if (isFavorite) {
        // Supprimer des favoris
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('video_id', videoId);
        
        if (error) throw error;
        
        // La suppression sera gérée par le hook realtime
        
        toast({
          title: 'Removed from favorites',
          description: 'Video has been removed from your favorites',
        });
      } else {
        // Ajouter aux favoris ET sauvegarder la vidéo complète
        const video = videos.find(v => v.id === videoId);
        if (!video) {
          toast({
            title: 'Error',
            description: 'Video not found',
            variant: 'destructive',
          });
          return;
        }

        // Sauvegarder la vidéo complète dans user_videos
        const { error: videoError } = await supabase
          .from('user_videos')
          .upsert({
            video_id: video.id,
            title: video.title,
            thumbnail: video.thumbnail,
            channel_name: video.channelName,
            channel_avatar: video.channelAvatar,
            channel_id: video.channelId,
            views: video.views,
            published: video.published,
            published_date: video.publishedDate,
            duration: video.duration,
            added_by_user_id: user.id,
          });

        if (videoError) {
          console.error('Error saving video:', videoError);
        }

        // Ajouter aux favoris
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            video_id: videoId,
          });
        
        if (error) throw error;
        
        // L'ajout sera géré par le hook realtime
        
        toast({
          title: 'Added to favorites',
          description: 'Video has been added to your favorites',
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorites',
        variant: 'destructive',
      });
    }
  };
  
  const createPlaylist = async () => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to create playlists',
        variant: 'destructive',
      });
      return;
    }
    
    if (!newPlaylistName.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a name for your playlist',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('playlists')
        .insert({
          name: newPlaylistName.trim(),
          user_id: user.id,
        })
        .select();
      
      if (error) throw error;
      
      // La nouvelle playlist sera automatiquement ajoutée via le hook temps réel
      setSelectedPlaylist(data[0].id);
      
      toast({
        title: 'Playlist created',
        description: `"${newPlaylistName}" has been created`,
      });
      
      setNewPlaylistName('');
      setIsCreatingPlaylist(false);
    } catch (error) {
      console.error('Error creating playlist:', error);
      toast({
        title: 'Error',
        description: 'Failed to create playlist',
        variant: 'destructive',
      });
    }
  };
  
  const deletePlaylist = async (playlistId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('playlists')
        .delete()
        .eq('id', playlistId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // La playlist sera automatiquement supprimée via le hook temps réel
      if (selectedPlaylist === playlistId) {
        setSelectedPlaylist(playlists.length > 1 ? playlists[0].id : null);
      }
      
      toast({
        title: 'Playlist deleted',
        description: 'Playlist has been deleted',
      });
    } catch (error) {
      console.error('Error deleting playlist:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete playlist',
        variant: 'destructive',
      });
    }
  };
  
  const addToPlaylist = useCallback(async (videoId: string, playlistId: string) => {
    if (!user) return;
    
    try {
      const isInPlaylist = playlistVideos[playlistId]?.includes(videoId);
      
      if (isInPlaylist) {
        toast({
          title: 'Déjà dans la playlist',
          description: 'Cette vidéo est déjà dans la playlist sélectionnée',
        });
        return;
      }

      // Ajouter à la playlist ET sauvegarder la vidéo complète
      const video = videos.find(v => v.id === videoId);
      if (!video) {
        toast({
          title: 'Error',
          description: 'Video not found',
          variant: 'destructive',
        });
        return;
      }

      // Sauvegarder la vidéo complète dans user_videos
      const { error: videoError } = await supabase
        .from('user_videos')
        .upsert({
          video_id: video.id,
          title: video.title,
          thumbnail: video.thumbnail,
          channel_name: video.channelName,
          channel_avatar: video.channelAvatar,
          channel_id: video.channelId,
          views: video.views,
          published: video.published,
          published_date: video.publishedDate,
          duration: video.duration,
          added_by_user_id: user.id,
        });

      if (videoError) {
        console.error('Error saving video:', videoError);
      }
      
      // Ajouter à la playlist
      const { error } = await supabase
        .from('playlist_items')
        .insert({
          playlist_id: playlistId,
          video_id: videoId,
          user_id: user.id,
        });
      
      if (error) throw error;
      
      // La mise à jour sera automatique via le hook temps réel
      
      toast({
        title: 'Ajouté à la playlist',
        description: 'Vidéo ajoutée à votre playlist',
      });
    } catch (error) {
      console.error('Error adding to playlist:', error);
      toast({
        title: 'Error',
        description: 'Impossible d\'ajouter la vidéo à la playlist',
        variant: 'destructive',
      });
    }
  }, [user, playlistVideos, toast, videos]);
  
  const removeFromPlaylist = useCallback(async (videoId: string, playlistId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('playlist_items')
        .delete()
        .eq('playlist_id', playlistId)
        .eq('video_id', videoId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // La mise à jour sera automatique via le hook temps réel
      
      toast({
        title: 'Retiré de la playlist',
        description: 'Vidéo supprimée de votre playlist',
      });
    } catch (error) {
      console.error('Error removing from playlist:', error);
      toast({
        title: 'Error',
        description: 'Impossible de supprimer la vidéo de la playlist',
        variant: 'destructive',
      });
    }
  }, [user, toast]);
  
  const clearPlaylist = async (playlistId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('playlist_items')
        .delete()
        .eq('playlist_id', playlistId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // La mise à jour sera automatique via le hook temps réel
      
      toast({
        title: 'Playlist vidée',
        description: 'Toutes les vidéos ont été supprimées de cette playlist',
      });
    } catch (error) {
      console.error('Error clearing playlist:', error);
      toast({
        title: 'Error',
        description: 'Impossible de vider la playlist',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <div className="min-h-screen bg-youtube-light dark:bg-youtube-dark">
      <Navbar />
      
      <main className="page-container">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Mes Collections</h1>
        </div>
        
        <Tabs defaultValue="favorites">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="favorites" className="flex items-center gap-2">
                <Heart size={16} /> Favoris
              </TabsTrigger>
              <TabsTrigger value="playlists" className="flex items-center gap-2">
                <Folder size={16} /> Playlists
              </TabsTrigger>
            </TabsList>
            
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => setIsCreatingPlaylist(true)}
            >
              <Plus size={16} /> Nouvelle Playlist
            </Button>
            
            <PlaylistDialog
              isOpen={isCreatingPlaylist}
              onOpenChange={setIsCreatingPlaylist}
              playlistName={newPlaylistName}
              onPlaylistNameChange={setNewPlaylistName}
              onCreatePlaylist={createPlaylist}
            />
          </div>
          
          <TabsContent value="favorites">
            <FavoritesList 
              videos={videos}
              favorites={favorites}
              isLoading={isLoadingPlaylists}
              onRemoveFavorite={toggleFavorite}
            />
          </TabsContent>
          
          <TabsContent value="playlists">
            {isLoadingPlaylists ? (
              <div className="text-center py-16">Chargement des playlists...</div>
            ) : playlists.length > 0 ? (
              <div className="space-y-6">
                <PlaylistSelector 
                  playlists={playlists}
                  selectedPlaylist={selectedPlaylist}
                  playlistVideos={playlistVideos}
                  onSelectPlaylist={setSelectedPlaylist}
                  onDeletePlaylist={deletePlaylist}
                />
                
                <PlaylistContent 
                  selectedPlaylist={selectedPlaylist}
                  playlists={playlists}
                  playlistVideos={playlistVideos}
                  videos={videos}
                  isLoading={loading}
                  onRemoveFromPlaylist={removeFromPlaylist}
                  onClearPlaylist={clearPlaylist}
                />
              </div>
            ) : (
              <div className="text-center py-16 bg-white dark:bg-youtube-dark shadow-sm rounded-lg border border-gray-200 dark:border-gray-800">
                <h2 className="text-xl font-medium mb-4">Pas de playlists</h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  Vous n'avez pas encore créé de playlists. Cliquez sur "Nouvelle Playlist" pour commencer.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {!loading && videos.length > 0 && (
          <RecommendedVideos
            videos={videos}
            onAddToPlaylist={addToPlaylist}
            onToggleFavorite={toggleFavorite}
          />
        )}
      </main>
    </div>
  );
};

const PlaylistsPage = () => {
  return (
    <PlaylistProvider>
      <PlaylistsPageContent />
    </PlaylistProvider>
  );
};

export default PlaylistsPage;
